import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarClock, Download, Loader2, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import BiChart from "./BiChart";

const VIS_LABEL = { table: "Table", bar: "Bar chart", line: "Line chart", pie: "Pie chart" };
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const BiTemplates = ({ templates, reload, onEdit }) => {
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [frequency, setFrequency] = useState("weekly");
  const [hour, setHour] = useState(7);
  const [dayOfWeek, setDayOfWeek] = useState(0);

  const runTemplate = async (template) => {
    setRunning(template.template_id);
    try {
      const { data } = await axios.post(
        `${API}/bi/templates/${template.template_id}/run`,
        {},
        { withCredentials: true }
      );
      setResult(data);
      toast.success(`${template.name}: ${data.rows.length} row(s)`);
    } catch (error) {
      console.error("Template run failed", error);
      toast.error(error?.response?.data?.detail || "Could not run that template");
    } finally {
      setRunning(null);
    }
  };

  const deleteTemplate = async (template) => {
    try {
      await axios.delete(`${API}/bi/templates/${template.template_id}`, { withCredentials: true });
      toast.success("Template deleted");
      reload();
    } catch (error) {
      console.error("Template delete failed", error);
      toast.error("Could not delete that template");
    }
  };

  const createSchedule = async () => {
    try {
      await axios.post(
        `${API}/bi/schedules`,
        {
          template_id: scheduleFor.template_id,
          frequency,
          hour: Number(hour),
          day_of_week: Number(dayOfWeek),
        },
        { withCredentials: true }
      );
      toast.success(`${scheduleFor.name} scheduled ${frequency}`);
      setScheduleFor(null);
      reload();
    } catch (error) {
      console.error("Schedule failed", error);
      toast.error(error?.response?.data?.detail || "Could not schedule that report");
    }
  };

  const exportResult = async () => {
    try {
      const response = await axios.post(
        `${API}/bi/export/xlsx`,
        {
          title: result.template.name,
          columns: result.columns,
          rows: result.rows,
          summary: result.summary || {},
          chart: result.template.visualization === "table" ? null : result.template.visualization,
        },
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${result.template.name.replace(/\s+/g, "-").toLowerCase()}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Could not build the Excel file");
    }
  };

  const chartRows = result
    ? result.rows.map((row) => Object.fromEntries(result.columns.map((column, index) => [column, row[index]])))
    : [];

  return (
    <div className="space-y-6" data-testid="bi-templates">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map((template) => (
          <Card key={template.template_id} data-testid={`bi-template-${template.template_id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm">{template.name}</CardTitle>
                <Badge variant="secondary" className="text-[10px] shrink-0">
                  {template.is_builtin ? "Built-in" : "Custom"}
                </Badge>
              </div>
              <CardDescription className="text-xs">{template.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                {VIS_LABEL[template.visualization] || template.visualization} · {template.report_type.replace("_", " ")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={running === template.template_id}
                  onClick={() => runTemplate(template)}
                  data-testid={`bi-template-run-${template.template_id}`}
                >
                  {running === template.template_id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  Run
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => onEdit(template)}
                  data-testid={`bi-template-edit-${template.template_id}`}
                >
                  Open in builder
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setScheduleFor(template)}
                  data-testid={`bi-template-schedule-${template.template_id}`}
                >
                  <CalendarClock className="h-3.5 w-3.5" />
                  Schedule
                </Button>
                {!template.is_builtin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteTemplate(template)}
                    data-testid={`bi-template-delete-${template.template_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {result && (
        <Card data-testid="bi-template-result">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">{result.template.name}</CardTitle>
                <CardDescription>{result.rows.length} row(s)</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={exportResult} data-testid="bi-template-export">
                <Download className="h-4 w-4" />
                Export to Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {Object.entries(result.summary || {}).map(([key, value]) => (
                <Badge key={key} variant="secondary" className="text-[11px]">
                  {key}: {String(value)}
                </Badge>
              ))}
            </div>
            <BiChart
              type={result.template.visualization}
              columns={result.columns}
              rows={chartRows}
              valueKeys={result.columns.slice(1)}
            />
          </CardContent>
        </Card>
      )}

      <Dialog open={Boolean(scheduleFor)} onOpenChange={(open) => !open && setScheduleFor(null)}>
        <DialogContent className="bg-white dark:bg-slate-900" data-testid="bi-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule “{scheduleFor?.name}”</DialogTitle>
            <DialogDescription>
              The report runs automatically and lands in the Scheduled tab, ready to download.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">How often</Label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="bi-schedule-frequency"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {frequency === "weekly" && (
                <div>
                  <Label className="text-xs">Day</Label>
                  <select
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(e.target.value)}
                    className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                    data-testid="bi-schedule-day"
                  >
                    {DAYS.map((day, index) => (
                      <option key={day} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs">Hour (UTC)</Label>
                <select
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="bi-schedule-hour"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {String(i).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button onClick={createSchedule} className="w-full" data-testid="bi-schedule-confirm">
              Schedule it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BiTemplates;
